<template>
  <div
    :class="[
      'promo-card-beta',
      cardTypeClass,
      { 'is-selected': isSelect },
      { 'is-not-allowed': isNotAllowed }
    ]"
    :style="cardBgStyle"
    @click="$emit('click')"
  >
    <!-- Left section: Icon, Title, Price -->
    <div class="card-left">
      <!-- Icon -->
      <div class="card-icon">
        <img v-if="cardType === 'buildBundle'" :src="iconGift" alt="Build Bundle" />
        <img v-else-if="cardType === 'totalTox'" :src="iconTestTubes" alt="Total Tox" />
        <img v-else-if="cardType === 'allVw'" :src="iconCheckmark" alt="All VW" />
      </div>

      <!-- Title and Price -->
      <div class="card-content">
        <div class="card-title-row">
          <h3 class="card-title">{{ panel.title }}</h3>
          <span v-if="showPrice" class="card-price-inline">{{ formattedPrice }}</span>
        </div>
        <p v-if="subtitleText" class="card-subtitle">{{ subtitleText }}</p>
      </div>
    </div>

    <!-- Right section: Button only -->
    <div class="card-right">
      <button
        class="card-btn"
        :class="{ 'btn-added': isSelect }"
      >
        <svg v-if="isSelect" class="check-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.00004 10.78L3.22004 8L2.27337 8.94L6.00004 12.6667L14 4.66667L13.06 3.72667L6.00004 10.78Z" fill="white"/>
        </svg>
        {{ buttonText }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import bgBlue from "@/assets/orderTest/order-page/promoPanel/highlight-card-bg-blue.svg";
import bgGreen from "@/assets/orderTest/order-page/promoPanel/highlight-card-bg-green.svg";
import bgWhite from "@/assets/orderTest/order-page/promoPanel/highlight-card-bg-white.svg";
import iconGift from "@/assets/orderTest/order-page/promoPanel/icon-gift.svg";
import iconTestTubes from "@/assets/orderTest/order-page/promoPanel/icon-test-tubes.svg";
import iconCheckmark from "@/assets/orderTest/order-page/promoPanel/icon-checkmark-green.svg";

export default {
  name: "PromoPanelCardBeta",
  emits: ["click"],
  data() {
    return {
      bgBlue,
      bgGreen,
      bgWhite,
      iconGift,
      iconTestTubes,
      iconCheckmark
    };
  },
  props: {
    panel: {
      type: Object,
      required: true
    },
    isSelect: {
      type: Boolean,
      default: false
    },
    isNotAllowed: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState(["currency"]),
    ...mapState("orderTest", ["isDbsVersion"]),
    ...mapGetters("orderTest/panels", ["bundleList"]),
    
    cardType() {
      const bundleId = this.panel.bundleIdList?.[0];
      // Build Bundle: 18008 (serum) or 18011 (DBS)
      if (bundleId === 18008 || bundleId === 18011) {
        return "buildBundle";
      }
      // Total Tox Burden: 18014
      if (bundleId === 18014) {
        return "totalTox";
      }
      // All VW Package: 1 (serum) or 6 (DBS)
      if (bundleId === 1 || bundleId === 6) {
        return "allVw";
      }
      // Default to white card style
      return "totalTox";
    },

    cardTypeClass() {
      return `card-type-${this.cardType}`;
    },

    cardBgStyle() {
      let bgImage = this.bgWhite;
      if (this.cardType === "buildBundle") {
        bgImage = this.bgBlue;
      } else if (this.cardType === "allVw") {
        bgImage = this.bgGreen;
      }
      return {
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    },

    buttonText() {
      if (this.isSelect) {
        return this.$t("OrderPage[\"Added\"]");
      }
      if (this.cardType === "buildBundle") {
        return this.$t("OrderPage[\"Build Now\"]") || "Build Now";
      }
      if (this.cardType === "totalTox") {
        return this.$t("OrderPage[\"Order Now\"]");
      }
      return this.$t("OrderPage[\"Add to Cart\"]") || "Add to Cart";
    },

    showPrice() {
      return this.cardType !== "buildBundle" && this.cardType !== "allVw";
    },

    formattedPrice() {
      // Get price from bundleList using bundleIdList
      if (this.panel.bundleIdList && this.panel.bundleIdList.length > 0) {
        const bundleId = this.panel.bundleIdList[0];
        const bundle = this.bundleList.find(b => b.id === bundleId || b.bundleId === bundleId);
        if (bundle && bundle.price !== undefined) {
          return this.currency.symbol + bundle.price.toFixed(2);
        }
      }
      return '';
    },

    subtitleText() {
      return this.panel.subTitle || this.panel.subtitle || this.panel.description || "";
    }
  }
};
</script>

<style scoped>
.promo-card-beta {
  position: relative;
  width: 100%;
  height: 60px;
  border-radius: 11px;
  padding: 12px 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.08), 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.promo-card-beta:hover {
  box-shadow: 0 12px 28px 0 rgba(0, 0, 0, 0.12), 0 2px 6px 0 rgba(0, 0, 0, 0.08);
}

.promo-card-beta.is-not-allowed {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Left content area */
.card-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

/* Icon container */
.card-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
  border-radius: 0;
}

.card-icon img {
  width: 20px;
  height: 20px;
}

/* Content area (title + subtitle) */
.card-content {
  flex: 1;
  min-width: 0;
}

/* Right content area - button only */
.card-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* Content area (title + price) */
.card-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.card-title-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  justify-content: flex-start;
}

/* Title */
.card-title {
  color: var(--TrueBlack, #333);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin: 0;
  flex: 0 1 auto;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.1px;
}

.card-subtitle {
  color: var(--TrueBlack, #666666);
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Price inline (next to title) */
.card-price-inline {
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.5px;
  color: var(--Blue-60-hover, #0C96B1);
  white-space: nowrap;
}

/* Button */
.card-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: auto;
  padding: 0 2px;
  border-radius: 0;
  border: none;
  background: transparent;
  color: var(--VibrantDarkBlue, #004879);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: none;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* Blue button (Build Bundle, Total Tox) */
.card-type-buildBundle .card-btn,
.card-type-totalTox .card-btn {
  color: var(--VibrantDarkBlue, #004879);
}

/* Card hover triggers button hover */
.card-type-buildBundle:hover .card-btn,
.card-type-totalTox:hover .card-btn {
  color: var(--New_Library_HoverGreen, #20A7C1);
}

.card-type-buildBundle:active .card-btn,
.card-type-totalTox:active .card-btn {
  color: #003459;
}

.card-type-buildBundle .card-btn.btn-added,
.card-type-totalTox .card-btn.btn-added {
  color: #003459;
}

/* Green button (All VW) */
.card-type-allVw .card-btn {
  color: var(--color-Functional-Green-status-000, #046629);
}

/* Card hover triggers button hover */
.card-type-allVw:hover .card-btn {
  color: #2D8B4E;
}

.card-type-allVw:active .card-btn {
  color: #034D1F;
}

.card-type-allVw .card-btn.btn-added {
  color: #034D1F;
}

.check-icon {
  flex-shrink: 0;
}

/* Selected state */
.promo-card-beta.is-selected {
  box-shadow: 0 0 0 3px rgba(74, 74, 74, 0.25), 0 4px 20px 0 rgba(0, 0, 0, 0.08);
}
</style>
